/* eslint-disable @next/next/no-img-element */
import { useRouter } from 'next/router';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import FAQs from 'src/common/components/faqs';
import {
  HelpPageContainer,
  Title,
  IconContainer,
  HelpNavbar,
  HelpContent,
} from './styles';
import { getDomainOrigin } from 'src/common/utils/serverConfig';
import { getRequest } from 'src/common/utils/api/request';
import { internationalTravelUrls, UrlType } from 'src/common/utils/api/urls';
import ErrorPage from 'src/common/components/error-page';
import Skeleton from './Skeleton';
import { ASSETS } from 'src/common/utils/config';
import {
  TravelInsuranceViewEvents,
  TravelInsurancePages,
  TravelInsuranceTrackEvents,
  TRAVEL,
} from 'src/common/utils/segment/types';
import {
  pageEvent,
  trackEvent,
  waitForAnalyticsLoad,
} from '@acko-tech/building-blocks.ui.acko-analytics';
import { getUtmParams, useRouterPush } from 'src/common/custom-hooks';
import { StateToPageMapper } from 'src/international-insurance/config';
import { Row, Text } from 'src/common/styles';
import { useViewport } from 'src/common/utils/responsive';

const HelpPage = ({
  styles = {},
  showFaqsOnly = false,
  hideErrorView = false,
}: any) => {
  const router = useRouter();
  const journeyId = router?.query?.journeyId;
  const fromPage = router?.query?.from || '';
  const { isMobile } = useViewport();
  const isApp = router?.query?.platform?.includes('app');

  const [helpData, setHelpData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState<any>(null);
  const pushRoute = useRouterPush();

  useEffect(() => {
    getFaqs();
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && !!helpData) {
      const userDetails = JSON.parse(
        localStorage?.getItem('userDetails') as string
      );

      waitForAnalyticsLoad().then(() => {
        pageEvent(TravelInsuranceViewEvents.VIEW_PAGE_INT_HELP, {
          journey: 'buy',
          product: 'travel',
          sub_product_name: 'international_travel',
          email: !!userDetails?.email ? userDetails?.email : undefined,
          phone: !!userDetails?.phone ? userDetails?.phone : undefined,
          platform: isApp ? 'app' : isMobile ? 'mweb' : 'web',
          page: TravelInsurancePages.INT_TRAVEL_HELP_SCREEN,
          from_page: fromPage
            ? StateToPageMapper[fromPage as string]
            : undefined,
          proposal_id: journeyId,
          ...getUtmParams(),
        });
      });
    }
  }, [helpData]);

  const getFaqs = async () => {
    setIsLoading(true);
    try {
      const res = await getRequest(
        `${getDomainOrigin()}${internationalTravelUrls.generic(
          UrlType.FETCH_FAQS
        )}`
      );
      setIsLoading(false);
      setIsError(null);
      setHelpData(res?.data?.data);
    } catch (error) {
      setIsLoading(false);
      setIsError(error);

      if (typeof window !== 'undefined' && !!window?.newrelic) {
        window?.newrelic?.noticeError(error, {
          url: window?.location?.href,
          journeyId,
        });
      }
    }
  };

  const handleBack = () => {
    let userDetails = { email: '', phone: '' };

    if (typeof window !== 'undefined') {
      userDetails = JSON.parse(localStorage?.getItem('userDetails') as string);
    }
    trackEvent(TravelInsuranceTrackEvents.TAP_BTN_INT_BACK, {
      journey: TRAVEL.JOURNEY,
      product: TRAVEL.PRODUCT,
      sub_product_name: TRAVEL.SUB_PRODUCT_NAME,
      email: !!userDetails?.email ? userDetails?.email : undefined,
      phone: !!userDetails?.phone ? userDetails?.phone : undefined,
      platform: isApp ? 'app' : isMobile ? 'mweb' : 'web',
      proposal_id: journeyId,
      page: TravelInsurancePages.INT_TRAVEL_HELP_SCREEN,
      from_page: fromPage ? StateToPageMapper[fromPage as string] : undefined,
      ...getUtmParams(),
    });

    if (router?.query?.policyId !== undefined) {
      return pushRoute(`/pdp/${router?.query?.policyId}`);
    }

    return pushRoute(`/${router?.query?.journeyId}`);
  };

  if (!!isError && !hideErrorView) {
    return <ErrorPage error={isError} handleClick={() => handleBack} />;
  }

  return (
    <HelpPageContainer>
      {showFaqsOnly ? (
        <></>
      ) : (
        <HelpNavbar>
          {isMobile ? (
            <IconContainer onClick={handleBack}>
              <img
                src={ASSETS.BACK_ARROW}
                width={'24px'}
                height={'24px'}
                alt="back"
              />
            </IconContainer>
          ) : (
            <IconContainer onClick={() => router?.push('/')}>
              <img
                src={ASSETS.ACKO_LOGO_DARK}
                width={'auto'}
                height={'24px'}
                alt="back"
              />
            </IconContainer>
          )}
        </HelpNavbar>
      )}
      <HelpContent contentStyles={styles?.contentStyles}>
        {isLoading ? (
          <Skeleton />
        ) : (
          <>
            {helpData && !showFaqsOnly && (
              <div>
                <Title>{helpData?.page_title}</Title>
                <Row
                  align={'center'}
                  margin={'24px 0 0 0'}
                  gap={'24px'}
                  paddingRight={'0'}
                  paddingBottom={'0'}
                >
                  <Image src={ASSETS.ENVELOPE} height={'20px'} width={'20px'} />
                  <Text
                    size={'1rem'}
                    weight={'600'}
                    lineHeight={'1.375rem'}
                    color={'#104EA3'}
                  >
                    <a
                      href={`mailto:${helpData?.support_email_address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {helpData?.support_email_address}
                    </a>
                  </Text>
                </Row>
                <Row
                  margin={'20px 0 0 0'}
                  paddingRight={'0'}
                  paddingBottom={'0'}
                  gap={'24px'}
                >
                  <Image src={ASSETS.PHONE} height={'20px'} width={'20px'} />
                  <div>
                    <Row>
                      <Text
                        size={'1rem'}
                        weight={'600'}
                        lineHeight={'1.375rem'}
                        color={'#104EA3'}
                      >
                        <a
                          href={`tel:${helpData?.domestic_phone_number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {helpData?.domestic_phone_number}
                        </a>
                      </Text>
                    </Row>
                  </div>
                </Row>
              </div>
            )}
            <FAQs
              title={helpData?.page_sub_title}
              helpData={helpData?.faq_list}
              styles={styles}
            />
          </>
        )}
      </HelpContent>
    </HelpPageContainer>
  );
};

export default HelpPage;
